import React from 'react';
import { Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Modal from '../../../../components/Modal';
import { PreviewItem, PreviewResponse } from '../../../../lib/api/settlementDateAdjustment';
import {
  PREVIEW_EXCLUDED_REASON_LABEL,
  SETTLEMENT_ITEM_TYPE_LABEL,
} from '../../constants/labels';
import { AfterDueDateCell } from './AfterDueDateCell';
import {
  StyledAfterCell,
  StyledBeforeCell,
  StyledSummary,
  StyledTabs,
} from './styles';

type Props = {
  open: boolean;
  result: PreviewResponse | null;
  executing?: boolean;
  /**
   * 운영자가 행별로 직접 지정한 dueDate. 키는 settlementItemId.
   * 값이 없는 행은 서버 자동 계산값(`item.afterDueDate`) 그대로 사용.
   */
  overrideMap: Map<number, string>;
  onOverrideChange: (settlementItemId: number, dueDate: string | null) => void;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * 일괄 변경 미리보기 모달.
 *
 * 상단은 건수 요약, 하단은 4개 탭(적용 대상 / 제외 / 영업일 자동 보정 / 활성 배치 중복).
 * - 적용 대상 탭: 변경 전/후 컬럼 그룹(회색/파랑 배경)으로 실제 변경값 비교
 * - 제외 탭: 제외 사유(정산 완료 등) 한국어 라벨
 * - 영업일 자동 보정 탭: shift 결과가 주말/공휴일이라 다음 영업일로 밀린 건의 원본(raw) ↔ 보정된 최종 날짜 비교
 * - 활성 배치 중복 탭: 해당 item이 이미 포함된 다른 배치 ID
 */
export const PreviewModal = ({
  open,
  result,
  executing,
  overrideMap,
  onOverrideChange,
  onCancel,
  onConfirm,
}: Props): React.JSX.Element => {
  // ─── 탭1: 적용 대상 ─────────────────────────────────
  const appliedColumns: ColumnsType<PreviewItem> = [
    { title: '정산번호', dataIndex: 'id', width: 90, fixed: 'left' },
    { title: '브랜드 ID', dataIndex: 'storeId', width: 100 },
    {
      title: '구분',
      dataIndex: 'type',
      width: 110,
      render: (v: PreviewItem['type']) => SETTLEMENT_ITEM_TYPE_LABEL[v] ?? v,
    },
    {
      title: '변경 전',
      className: 'before-group',
      children: [
        {
          title: '정산 기준일',
          dataIndex: 'beforeBaseDate',
          width: 140,
          className: 'before-cell',
          render: (v: string) => <StyledBeforeCell>{v}</StyledBeforeCell>,
        },
        {
          title: '정산 예정일',
          dataIndex: 'beforeDueDate',
          width: 140,
          className: 'before-cell',
          render: (v: string) => <StyledBeforeCell>{v}</StyledBeforeCell>,
        },
      ],
    },
    {
      title: '변경 후',
      className: 'after-group',
      children: [
        {
          title: '정산 기준일',
          dataIndex: 'afterBaseDate',
          width: 140,
          className: 'after-cell',
          render: (v: string) => <StyledAfterCell>{v}</StyledAfterCell>,
        },
        {
          title: '정산 예정일',
          key: 'afterDueDate',
          width: 220,
          className: 'after-cell',
          render: (_: unknown, row: PreviewItem) => (
            <AfterDueDateCell
              row={row}
              override={overrideMap.get(row.id)}
              onChange={onOverrideChange}
            />
          ),
        },
      ],
    },
  ];

  // ─── 탭2: 제외 대상 ─────────────────────────────────
  const excludedColumns: ColumnsType<{ id: number; reason: string }> = [
    { title: '정산번호', dataIndex: 'id', width: 140 },
    {
      title: '제외 사유',
      dataIndex: 'reason',
      render: (v: string) => <Tag color="default">{PREVIEW_EXCLUDED_REASON_LABEL[v] ?? v}</Tag>,
    },
  ];

  // ─── 탭3: 영업일 자동 보정 ───────────────────────────
  // shift 결과 dueDate 가 주말/공휴일이라 다음 영업일로 자동 이동된 건들.
  // 운영자가 "+5일 요청했는데 왜 실제는 +7일로 밀렸지?" 의문을 바로 해결하도록
  // raw(보정 전) vs 보정된 최종 dueDate 를 나란히 보여준다.
  const adjustedItems = result?.items.filter((i) => i.dueDateAdjusted) ?? [];
  const adjustedColumns: ColumnsType<PreviewItem> = [
    { title: '정산번호', dataIndex: 'id', width: 90 },
    { title: '브랜드 ID', dataIndex: 'storeId', width: 100 },
    {
      title: '변경 전 정산 예정일',
      dataIndex: 'beforeDueDate',
      width: 140,
      render: (v: string) => <StyledBeforeCell>{v}</StyledBeforeCell>,
    },
    {
      title: '보정 전 (주말/공휴일)',
      key: 'raw',
      width: 160,
      render: (_: unknown, r: PreviewItem) =>
        r.dueDateAdjusted ? (
          <Tag color="gold">
            {r.dueDateAdjusted.rawDate}
            {r.dueDateAdjusted.reason === 'holiday'
              ? ` · 공휴일${r.dueDateAdjusted.holidayMemo ? ` (${r.dueDateAdjusted.holidayMemo})` : ''}`
              : ' · 주말'}
          </Tag>
        ) : null,
    },
    {
      title: '보정 후 정산 예정일 (영업일)',
      dataIndex: 'afterDueDate',
      width: 180,
      render: (v: string) => <StyledAfterCell>{v}</StyledAfterCell>,
    },
  ];

  // ─── 탭4: 활성 배치 중복 ─────────────────────────
  const activeBatchItems = result?.items.filter((i) => i.activeBatchId) ?? [];
  const activeBatchColumns: ColumnsType<PreviewItem> = [
    { title: '정산번호', dataIndex: 'id', width: 90 },
    { title: '브랜드 ID', dataIndex: 'storeId', width: 100 },
    {
      title: '변경 후 정산 기준일',
      dataIndex: 'afterBaseDate',
      width: 160,
      render: (v: string) => <StyledAfterCell>{v}</StyledAfterCell>,
    },
    {
      title: '이미 적용 중인 배치 ID',
      dataIndex: 'activeBatchId',
      ellipsis: true,
      render: (v?: string) => (v ? <Tag color="orange">{v}</Tag> : '-'),
    },
  ];

  const tabItems = result
    ? [
        {
          key: 'applied',
          label: `적용 대상 (${result.summary.totalCount})`,
          children: (
            <Table<PreviewItem>
              size="small"
              rowKey="id"
              dataSource={result.items}
              columns={appliedColumns}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: 'max-content' }}
              bordered
              locale={{ emptyText: '적용 대상이 없습니다.' }}
            />
          ),
        },
        {
          key: 'excluded',
          label: `제외 (${result.summary.excludedCount})`,
          children: (
            <Table<{ id: number; reason: string }>
              size="small"
              rowKey="id"
              dataSource={result.excluded}
              columns={excludedColumns}
              pagination={{ pageSize: 20 }}
              bordered
              locale={{ emptyText: '제외된 건이 없습니다.' }}
            />
          ),
        },
        {
          key: 'adjusted',
          label: `영업일 자동 보정 (${result.summary.dueDateAdjustedCount})`,
          children: (
            <Table<PreviewItem>
              size="small"
              rowKey="id"
              dataSource={adjustedItems}
              columns={adjustedColumns}
              pagination={{ pageSize: 20 }}
              bordered
              locale={{
                emptyText: '주말/공휴일 회피로 자동 보정된 건이 없습니다.',
              }}
            />
          ),
        },
        {
          key: 'activeBatch',
          label: `활성 배치 중복 (${result.summary.activeBatchCount})`,
          children: (
            <Table<PreviewItem>
              size="small"
              rowKey="id"
              dataSource={activeBatchItems}
              columns={activeBatchColumns}
              pagination={{ pageSize: 20 }}
              bordered
              locale={{
                emptyText: '다른 활성 배치와 중복되는 건이 없습니다.',
              }}
            />
          ),
        },
      ]
    : [];

  return (
    <Modal
      title="일괄 변경 미리보기"
      isOpen={open}
      width={1200}
      isFooter
      okText={executing ? '실행 중...' : result ? `${result.items.length}건 실행` : '실행'}
      cancelText="취소"
      onOk={onConfirm}
      onClose={onCancel}
      padding="20px"
      maskClosable={false}
      destroyOnClose
    >
      {result && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <StyledSummary>
            <dl>
              <dt>적용 대상</dt>
              <dd>
                <strong>{result.summary.totalCount}</strong>건
              </dd>
            </dl>
            <dl>
              <dt>제외</dt>
              <dd>{result.summary.excludedCount}건</dd>
            </dl>
            <dl className={result.summary.dueDateAdjustedCount > 0 ? 'alert' : ''}>
              <dt>영업일 보정</dt>
              <dd>{result.summary.dueDateAdjustedCount}건</dd>
            </dl>
            <dl className={result.summary.activeBatchCount > 0 ? 'alert' : ''}>
              <dt>활성 배치 중복</dt>
              <dd>{result.summary.activeBatchCount}건</dd>
            </dl>
            {overrideMap.size > 0 && (
              <dl className="alert">
                <dt>수동 지정</dt>
                <dd>{overrideMap.size}건</dd>
              </dl>
            )}
          </StyledSummary>

          <StyledTabs items={tabItems} defaultActiveKey="applied" />
        </Space>
      )}
    </Modal>
  );
};
